import { Array } from "@siteimprove/alfa-array";
import { Element, Query } from "@siteimprove/alfa-dom";
import { Map } from "@siteimprove/alfa-map";
import { Option } from "@siteimprove/alfa-option";
import { Err, Ok } from "@siteimprove/alfa-result";
import { Result } from "@siteimprove/alfa-result";
import { Selective } from "@siteimprove/alfa-selective";
import { Page } from "@siteimprove/alfa-web";
import axios, { AxiosError } from "axios";
import { Audit } from "../audit/index.js";
/**
 * Interacting with Siteimprove Intelligence Platform (SIP) API.
 *
 * @public
 */
export var SIP;
(function (SIP) {
    /** @internal */
    let Defaults;
    (function (Defaults) {
        Defaults.URL = "https://api.siteimprove.com/v2/a11y/AlfaDevCheck";
        Defaults.Title = "";
        Defaults.Name = undefined;
        function missingOptions(missing) {
            return `Error: Missing required option${missing.length === 1 ? "" : "s"}: ${missing.length === 1
                ? missing[0]
                : missing.length === 2
                    ? missing.join(" and ")
                    : missing.slice(0, -1).join(", ") +
                        ", and " +
                        missing[missing.length - 1]}. Provide ${missing.length === 1 ? "it" : "them"} and try again.`;
        }
        Defaults.missingOptions = missingOptions;
        Defaults.badCredentials = "Error: Invalid credentials. Verify the username and API key, then try again.";
    })(Defaults = SIP.Defaults || (SIP.Defaults = {}));
    async function upload(audit, options, override = {}) {
        const missing = [];
        if (options.userName === undefined) {
            missing.push("User name");
        }
        if (options.apiKey === undefined) {
            missing.push("API key");
        }
        if (options.siteID === undefined) {
            missing.push("Site ID");
        }
        if (missing.length > 0) {
            return Err.of([Defaults.missingOptions(missing)]);
        }
        const config = Metadata.axiosConfig(audit, options, override);
        if (config.isErr()) {
            return config.mapErr((error) => [error]);
        }
        try {
            // The request fail on 4XX and 5XX responses, plus anything that can possibly
            // go wrong with axios.
            // We could accept all and handle the errors directly, but since we would
            // still need a try…catch for the "anything that can possibly go wrong" part,
            // the benefit would be minimal.
            const axiosResponse = await axios.request({
                // The get is guarded by the test before the try.
                ...config.getUnsafe(),
                // We do not want to throw on 3XX redirections.
                validateStatus: (status) => status < 400,
            });
            const { pageReportUrl, preSignedUrl, id } = axiosResponse.data;
            await axios.request({
                ...S3.axiosConfig(id, preSignedUrl, audit),
                // We do not want to throw on 3XX redirections.
                validateStatus: (status) => status < 400,
            });
            return Ok.of(pageReportUrl);
        }
        catch (error) {
            return inspectAxiosError(error);
        }
    }
    SIP.upload = upload;
    function inspectAxiosError(error) {
        if ((error.response ?? undefined) !== undefined) {
            const { status } = error.response;
            if (status === 401) {
                // 401 are handled by the generic server, and we don't get custom error message
                return Err.of([Defaults.badCredentials]);
            }
            if (status >= 400 && status < 500) {
                // This is a client error, we can get our custom error message
                return Err.of(Array.filter(Array.map(error.response?.data?.details ?? [], (detail) => detail?.issue), (issue) => issue !== undefined));
            }
            if (status >= 500) {
                // This is a server error, we probably don't have a custom message,
                // but hopefully axios did the work for us.
                return Err.of([
                    `Server error (${status}): ${error.message}. Try again later or contact support if the issue persists.`,
                ]);
            }
        }
        if (error instanceof AxiosError && error.message !== undefined) {
            // This is another axios error, we hope they provide meaningful messages.
            return Err.of([
                `Network error: ${error.message}. Check your request parameters and try again. If the issue persists, contact support.`,
            ]);
        }
        // This is something else. It should really not happen since only axios
        // should have thrown something.
        return Err.of([
            `Unexpected error: ${error}. Try uploading again. If the problem continues, verify the file and contact support.`,
        ]);
    }
    /**
     * Handling the metadata request to the Siteimprove API.
     *
     * @internal
     */
    let Metadata;
    (function (Metadata) {
        /**
         * Prepare payload with metadata for creating pre-signed URL.
         *
         * @remarks
         * The timestamp must be formated as an ISO 8601 string.
         */
        function payload(audit, options, timestamp) {
            // Even though we may not need to deserialize the page, error handling
            // get messy if done upon need.
            return (
            // Retrieve or deserialize the page
            // We may waste a bit of time deserializing a page we won't need (if URL
            // and title are provided), but this streamlines error handling.
            (Page.isPage(audit.page) ? Ok.of(audit.page) : Page.from(audit.page)).map((page) => {
                const url = options.pageURL ?? page.response.url.toString();
                const PageUrl = typeof url === "string" ? url : url(page);
                const title = options.pageTitle ??
                    Query.getElementDescendants(page.document)
                        .filter(Element.isElement)
                        .find(Element.hasName("title"))
                        .map((title) => title.textContent())
                        .getOr(Defaults.Title);
                const PageTitle = typeof title === "string"
                    ? title
                    : title !== undefined
                        ? title(page)
                        : title;
                const commitInfo = Selective.of(options.commitInformation)
                    .if((Option.isOption), (info) => info)
                    .if((Result.isResult), (info) => info.ok())
                    .else(Option.from)
                    .get();
                const name = options.testName ?? Defaults.Name;
                const TestName = 
                // If the name is a string, use it, otherwise call the function on the
                // commit info, defaulting to the default name.
                typeof name === "string"
                    ? name
                    : name !== undefined
                        ? commitInfo.map(name).getOr(Defaults.Name)
                        : Defaults.Name;
                const result = {
                    RequestTimestamp: timestamp,
                    Version: audit.alfaVersion,
                    PageUrl,
                    PageTitle,
                    TestName,
                    ResultAggregates: (Map.isMap(audit.resultAggregates)
                        ? audit.resultAggregates.toJSON()
                        : audit.resultAggregates).map(([RuleId, data]) => ({
                        RuleId,
                        ...toCamelCase(data),
                    })),
                    Durations: toCamelCase(audit.durations),
                };
                commitInfo.forEach((info) => (result.CommitInformation = info));
                if (options.siteID !== undefined) {
                    result.SiteId = options.siteID;
                }
                return result;
            }));
        }
        Metadata.payload = payload;
        /**
         * Configure parameters of axios request
         */
        function params(url, apiKey, httpsAgent) {
            const config = {
                method: "post",
                maxBodyLength: Infinity,
                url,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Basic " + Buffer.from(apiKey).toString("base64"),
                },
            };
            if (httpsAgent !== undefined) {
                config.httpsAgent = httpsAgent;
            }
            return config;
        }
        Metadata.params = params;
        /**
         * Prepare the configuration for the axios request
         */
        function axiosConfig(audit, options, override) {
            const { url = Defaults.URL, timestamp = new Date().toISOString() } = override;
            return payload(audit, options, timestamp).map((payload) => ({
                ...params(url, 
                // If one of them is missing, the parent upload call should already
                // have filtered it out. It is easier to not assume so. In any case,
                // the upload itself should ultimately fail on a 403.
                `${options?.userName}:${options?.apiKey}`, override.httpsAgent),
                data: JSON.stringify(payload),
            }));
        }
        Metadata.axiosConfig = axiosConfig;
    })(Metadata = SIP.Metadata || (SIP.Metadata = {}));
    /**
     * Handling the data request to the pre-signed S3 URL.
     *
     * @internal
     */
    let S3;
    (function (S3) {
        /**
         * Prepare payload with metadata for creating pre-signed URL.
         */
        /**
         * Prepare payload with Alfa page and results
         *
         * @internal
         */
        function payload(Id, audit) {
            const serialisedAudit = Audit.isAudit(audit) ? audit.toJSON() : audit;
            return {
                Id,
                CheckResult: JSON.stringify(serialisedAudit.outcomes.filter((outcome) => outcome.outcome !== "cantTell")),
                Aspects: JSON.stringify(serialisedAudit.page),
            };
        }
        S3.payload = payload;
        /**
         * Configure parameters of axios request
         */
        function params(url) {
            return {
                method: "put",
                maxBodyLength: Infinity,
                url,
                headers: { "Content-Type": "application/json" },
            };
        }
        S3.params = params;
        /**
         * Prepare the configuration for the axios request
         */
        function axiosConfig(id, url, audit) {
            const value = payload(id, audit);
            return {
                ...params(url),
                data: new Blob([
                    // Calling JSON.stringify directly on the payload causes
                    // RangeError: Invalid string length
                    // on large payloads. Doing it piecewise (and manually) avoids the
                    // problem, or at least push it back to even larger payload where
                    // the individual pieces will also be too large. If we encounter
                    // this again, we may have to switch to a JSON streaming solution.
                    //
                    // We need to loop through JSON.stringify a second time to properly
                    // escape strings.
                    `{"Id":${JSON.stringify(value.Id)},"CheckResult":${JSON.stringify(value.CheckResult)},"Aspects":${JSON.stringify(value.Aspects)}}`,
                ], {
                    type: "application/json",
                }),
            };
        }
        S3.axiosConfig = axiosConfig;
    })(S3 = SIP.S3 || (SIP.S3 = {}));
})(SIP || (SIP = {}));
function toCamelCase(object) {
    // Between the weird type, the regexs and `Object.entries`, TypeScript is
    // just unable to do its magic.
    // @ts-ignore
    return Object.fromEntries(Object.entries(object).map(([key, value]) => [
        key
            // Uppercase every letter after a dash, removing the dash.
            .replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())
            // Uppercase the first letter.
            .replace(/^([a-z])/, (_, letter) => letter.toUpperCase()),
        value,
    ]));
}
//# sourceMappingURL=sip.js.map